import { trpc } from '@/api/tRpc';
import { useTenantReady } from '@/hooks/useTenantReady';
import { POLL_FAST, POLL_SLOW } from '@/lib/query-options';

/** DLQ type discriminator */
export type DlqType = 'transform' | 'loader' | 'skipped' | 'initial_load';

/**
 * Hook to fetch dead-letter queue messages with auto-refresh.
 * Waits for environment context to be available before making the API call.
 *
 * @param { DlqType } type The type of DLQ to query.
 * @param { number } limit Maximum number of messages to fetch.
 * @param { number } [mappingConfigId] Only messages of this mapping id; every message when left out.
 * @returns { object } Returns tRPC query result with DLQ messages.
 */
export const useDlqMessages = (type: DlqType, limit = 100, mappingConfigId?: number) => {
  const tenantReady = useTenantReady();

  return trpc.dlq.listMessages.useQuery({ type, limit, mappingConfigId }, { ...POLL_SLOW, enabled: tenantReady });
};

/**
 * Hook to check selected DLQ messages before a replay: whether their mapping was deleted, and whether
 * newer DLQ messages exist for the same mapping and row. Always fetched fresh, because the DLQ changes
 * while the replay dialog is open.
 *
 * @param { string[] } messageIds The ids of the messages to replay.
 * @param { boolean } enabled Whether the check should run, e.g. while the replay dialog is open.
 * @returns { object } Returns tRPC query result with one check per message found.
 */
export const useDlqReplayChecks = (messageIds: string[], enabled: boolean) => {
  const tenantReady = useTenantReady();

  return trpc.dlq.getReplayChecks.useQuery(
    { messageIds },
    { gcTime: 0, staleTime: 0, enabled: tenantReady && enabled && messageIds.length > 0 },
  );
};

/**
 * Hook to fetch dead-letter queue statistics with auto-refresh.
 * Waits for environment context to be available before making the API call.
 *
 * @param { DlqType } type The type of DLQ to query.
 * @returns { object } Returns tRPC query result with DLQ statistics.
 */
export const useDlqStats = (type: DlqType) => {
  const tenantReady = useTenantReady();

  return trpc.dlq.getStats.useQuery({ type }, { ...POLL_FAST, enabled: tenantReady });
};

/**
 * Hook to retry dead-letter queue messages.
 *
 * @returns { object } Returns mutation object for retrying DLQ messages.
 */
export const useRetryDlqMessages = () => {
  const utils = trpc.useUtils();

  return trpc.dlq.retryMessages.useMutation({
    onSuccess: () => {
      utils.dlq.listMessages.invalidate();
      utils.dlq.getStats.invalidate();
      utils.monitoring.overview.invalidate();
    },
  });
};

/**
 * Hook to purge dead-letter queue messages.
 *
 * @returns { object } Returns mutation object for purging DLQ messages.
 */
export const usePurgeDlqMessages = () => {
  const utils = trpc.useUtils();

  return trpc.dlq.purgeMessages.useMutation({
    onSuccess: () => {
      utils.dlq.listMessages.invalidate();
      utils.dlq.getStats.invalidate();
      utils.monitoring.overview.invalidate();
    },
  });
};

/**
 * Hook to acknowledge a dead-letter queue message.
 *
 * @returns { object } Returns mutation object for acknowledging DLQ messages.
 */
export const useAcknowledgeDlqMessage = () => {
  const utils = trpc.useUtils();

  return trpc.dlq.acknowledgeMessage.useMutation({
    onSuccess: () => {
      utils.dlq.listMessages.invalidate();
      utils.dlq.getStats.invalidate();
    },
  });
};

/**
 * Hook to negatively acknowledge a dead-letter queue message.
 *
 * @returns { object } Returns mutation object for NACKing DLQ messages.
 */
export const useNackDlqMessage = () => {
  const utils = trpc.useUtils();

  return trpc.dlq.nackMessage.useMutation({
    onSuccess: () => {
      utils.dlq.listMessages.invalidate();
    },
  });
};

const DLQ_TYPES: DlqType[] = ['transform', 'loader', 'skipped', 'initial_load'];

/**
 * Hook that returns the total message count across all DLQ types.
 * Queries stats for each type independently and sums the counts.
 *
 * @returns { number | undefined } Total DLQ message count, or undefined while loading.
 */
export const useDlqTotalCount = (): number | undefined => {
  const transform = useDlqStats('transform');
  const loader = useDlqStats('loader');
  const skipped = useDlqStats('skipped');
  const initialLoad = useDlqStats('initial_load');

  const results = [transform, loader, skipped, initialLoad];

  // Return undefined until all queries have resolved at least once
  if (results.some((r) => r.data === undefined)) {
    return undefined;
  }

  return DLQ_TYPES.reduce((sum, _, i) => sum + (results[i]?.data?.messageCount ?? 0), 0);
};
